import React, { useState } from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

interface MetricRowProps {
    label: string;
    value: number;
    total: number;
    color: string;
    suffix?: string;
    trend?: number; // positive = up, negative = down, 0 = flat
    urgent?: boolean;
    onClick?: () => void;
}

export const MetricRow: React.FC<MetricRowProps> = ({
    label, value, total, color, suffix, trend, urgent, onClick,
}) => {
    const [hovered, setHovered] = useState(false);
    const pct = total > 0 ? Math.round((value / total) * 100) : 0;

    return (
        <Box
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onClick={onClick}
            sx={{
                p: '10px 12px',
                borderRadius: '8px',
                border: `1px solid ${hovered ? color + '40' : '#E8ECF0'}`,
                bgcolor: hovered ? `${color}0F` : '#F4F6F9',
                cursor: onClick ? 'pointer' : 'default',
                transition: 'all 0.15s ease',
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography sx={{
                    fontSize: 12,
                    color: hovered ? color : '#64748B',
                    fontWeight: hovered ? 600 : 400,
                    transition: 'all 0.15s',
                }}>
                    {label}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {trend !== undefined && (
                        <Typography sx={{
                            fontSize: 10, fontWeight: 700,
                            color: trend > 0 ? '#E65100' : trend < 0 ? '#16A34A' : '#94A3B8',
                        }}>
                            {trend > 0 ? `+${trend}` : trend < 0 ? `${trend}` : '—'}
                        </Typography>
                    )}
                    <Typography sx={{
                        fontSize: 12, fontWeight: 700,
                        color: hovered ? color : '#1A2B3C',
                        transition: 'color 0.15s',
                    }}>
                        {value}
                        {suffix && (
                            <Typography component="span" sx={{ fontWeight: 400, color: '#94A3B8', ml: 0.5, fontSize: 12 }}>
                                {suffix}
                            </Typography>
                        )}
                    </Typography>
                    {onClick && (
                        <ArrowForwardIosIcon sx={{
                            fontSize: 10,
                            color,
                            opacity: hovered ? 1 : 0,
                            transform: hovered ? 'translateX(0)' : 'translateX(-4px)',
                            transition: 'all 0.15s',
                        }} />
                    )}
                </Box>
            </Box>
            <LinearProgress
                variant="determinate"
                value={pct}
                sx={{
                    height: 5, borderRadius: 3,
                    bgcolor: `${color}18`,
                    '& .MuiLinearProgress-bar': {
                        borderRadius: 3,
                        bgcolor: color,
                        transition: 'width 0.5s ease',
                    },
                }}
            />
        </Box>
    );
};
